<script setup lang="ts" generic="T extends Record<string, any>">
import type { SortingState } from "@tanstack/vue-table";
import type { TableEmptyStateType } from "~/utils/types";
import type { AwBulkActionProps, AwTableColumn, AwTableRow } from "@asap/design";

type Props = {
  data: T[];
  loading?: boolean;
  columns: AwTableColumn<T>[];
  tableKey?: string;
  isSelectable?: boolean;
  hasSelectAllCheckbox?: boolean;
  rowSelection?: Record<string, boolean>;
  enableRowSelection?: (row: AwTableRow<T>) => boolean;
  onSelect?: (row: AwTableRow<T>) => void;
  emptyStateType?: TableEmptyStateType;
  emptyStateResetFilters?: () => void;
  sorting?: SortingState;
  enableSorting?: boolean;
  manualSorting?: boolean;
  height?: string;
  hasBulkAction?: boolean;
  bulkActions?: AwBulkActionProps["actions"];
};

const props = withDefaults(defineProps<Props>(), {
  tableKey: "table-key",
  isSelectable: false,
  hasSelectAllCheckbox: false,
  emptyStateType: "no-data",
  emptyStateResetFilters: undefined,
  rowSelection: () => ({}),
  enableRowSelection: () => true,
  onSelect: () => {},
  sorting: () => [],
  enableSorting: true,
  manualSorting: false,
  height: "100vh",
  hasBulkAction: false,
  bulkActions: () => [],
});

const emit = defineEmits<{
  "update:rowSelection": [value: Record<string, boolean>];
  "update:sorting": [value: SortingState];
}>();

const bulkItems = defineModel<T[]>("bulkItems", { default: () => [] });

const { t } = useI18n();

const tableContainerRef = ref<HTMLElement>();

const sorting = computed({
  get: () => props.sorting,
  set: (value) => emit("update:sorting", value),
});

const rowSelection = computed({
  get: () => props.rowSelection,
  set: (value) => emit("update:rowSelection", value),
});

const rowSelectionOptions = computed(() => {
  return {
    enableRowSelection: props.enableRowSelection,
  };
});

const getEmptyStateIcon = () => {
  switch (props.emptyStateType) {
    case "no-data":
      return "i-ph-magnifying-glass-minus";
    case "no-data-with-filters":
      return "i-ph-funnel-x";
    case "error":
      return "i-ph-warning";
    default:
      return "i-ph-x-square";
  }
};

const getEmptyStateLabel = () =>
  props.emptyStateType === "error"
    ? t("common.tableInteractive.emptyState.errorLabel")
    : t("common.tableInteractive.emptyState.noDataLabel");

const getEmptyStateDescription = () => {
  switch (props.emptyStateType) {
    case "no-data-with-filters":
      return t("common.tableInteractive.emptyState.noDataWithFiltersDescription");
    case "error":
      return t("common.tableInteractive.emptyState.errorDescription");
    default:
      return t("common.tableInteractive.emptyState.noDataDescription");
  }
};
</script>

<template>
  <div ref="tableContainerRef" class="table-container relative">
    <hr />
    <div v-if="$slots.filters">
      <slot name="filters"></slot>
    </div>
    <hr v-if="$slots.filters" />

    <AwTable
      :key="tableKey"
      v-model:sorting="sorting"
      v-model:row-selection="rowSelection"
      :data="loading ? Array(20).fill({}) : data"
      :columns="columns"
      :is-selectable="isSelectable"
      :hide-select-all-checkbox="!hasSelectAllCheckbox"
      :on-select="onSelect"
      :loading="loading"
      :sticky="true"
      :row-selection-options="rowSelectionOptions"
      :sorting-options="{
        enableSorting: enableSorting,
        manualSorting: manualSorting,
      }"
    >
      <template #empty>
        <div class="empty-state-container">
          <AwEmptyState
            :label="getEmptyStateLabel()"
            :description="getEmptyStateDescription()"
            :actions="
              emptyStateResetFilters !== undefined && emptyStateType === 'no-data-with-filters'
                ? [{ label: $t('common.tableInteractive.emptyState.resetFilters'), onClick: emptyStateResetFilters }]
                : undefined
            "
          >
            <template #illustration>
              <AwIcon :name="getEmptyStateIcon()" class="w-12 h-12 text-gray-700 mb-4" />
            </template>
          </AwEmptyState>
        </div>
      </template>
      <template v-for="(_, slot) in $slots" :key="slot" #[slot]="scope">
        <slot :name="slot" v-bind="scope"></slot>
      </template>
    </AwTable>

    <div v-if="$slots.footer" class="table-footer">
      <slot name="footer"></slot>
    </div>

    <TableBulkAction
      v-model:selection="rowSelection"
      v-model:bulk-items="bulkItems"
      :show="hasBulkAction"
      :actions="bulkActions"
    />
  </div>
</template>

<style scoped>
.table-container {
  z-index: 0;
  display: grid;
  height: v-bind(height);
  grid-template-rows: auto auto 1fr auto;
  container-type: size;
}

.table-footer {
  bottom: 0;
  position: sticky;
  margin-top: auto;
  background: white;
}

.empty-state-container {
  min-height: calc(v-bind(height) / 2);
  max-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

:deep(tbody tr:has(.empty-state-container)):hover {
  background-color: transparent !important;
}

:deep(tbody tr:has(.empty-state-container)) {
  background-color: transparent !important;
}

:deep(tbody tr:has(.empty-state-container) td) {
  background-color: transparent !important;
}

:deep(tbody tr:has(.empty-state-container) td):hover {
  background-color: transparent !important;
}
</style>
